import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Bundle offer cards for the product form.
 * - Picking a card sets the product form quantity to the card's quantity.
 * - After the product form adds the main product, the card's extra products and gift are added,
 *   any missing main quantity is topped up, and the card's discount code is saved for checkout.
 * Each card is `[data-bundle-option]` with `data-quantity`, `data-extras` (comma separated variant ids),
 * `data-gift` (variant id) and `data-discount`.
 *
 * When a bigger bundle is picked, the add to cart tap is caught before any cart app (like the Zipify
 * cart drawer) can take it over, and every product in the bundle is added in one request.
 */
class CustomBundlePicker extends HTMLElement {
  #controller = new AbortController();
  #touchMoved = false;
  #adding = false;

  connectedCallback() {
    const { signal } = this.#controller;
    this.addEventListener('change', this.#syncQuantity, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    // Window capture runs before document-level listeners that cart apps use.
    // Some apps add to cart on touchend, so taps, clicks and keyboard presses are all caught.
    const options = { signal, capture: true, passive: false };
    window.addEventListener('touchstart', () => { this.#touchMoved = false; }, { signal, capture: true, passive: true });
    window.addEventListener('touchmove', () => { this.#touchMoved = true; }, { signal, capture: true, passive: true });
    window.addEventListener('touchend', (event) => { if (!this.#touchMoved) this.#onAddClick(event); }, options);
    window.addEventListener('click', this.#onAddClick, options);
    window.addEventListener('keydown', (event) => { if (event.key === 'Enter' || event.key === ' ') this.#onAddClick(event); }, options);

    if (!this.querySelector('[data-bundle-option] input:checked')) {
      const first = this.querySelector('[data-bundle-option] input');
      if (first instanceof HTMLInputElement) first.checked = true;
    }
    this.#syncQuantity();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
  }

  get #selected() {
    const input = this.querySelector('[data-bundle-option] input:checked');
    const option = input?.closest('[data-bundle-option]');
    return option instanceof HTMLElement ? option : null;
  }

  get #quantityInput() {
    const form = document.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`);
    const input = form?.querySelector('input[name="quantity"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  #syncQuantity = () => {
    const option = this.#selected;
    const input = this.#quantityInput;
    if (!option) return;
    if (!input) {
      this.dataset.express = 'on';
      return;
    }

    input.value = option.dataset.quantity || '1';
    input.dispatchEvent(new Event('change', { bubbles: true }));

    // Express checkout buttons skip the bundle extras, so hide them for bigger bundles.
    const simple = !option.dataset.extras && !option.dataset.gift && !option.dataset.discount && (Number(option.dataset.quantity) || 1) === 1;
    this.dataset.express = simple ? 'on' : 'off';
  };

  get #form() {
    const form = document.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"] form`);
    return form instanceof HTMLFormElement ? form : null;
  }

  /** True when the picked card adds more than one plain unit of the main product. */
  #isBigBundle(option) {
    return Boolean(option.dataset.extras || option.dataset.gift || option.dataset.discount) || (Number(option.dataset.quantity) || 1) > 1;
  }

  /** @param {Event} event */
  #onAddClick = async (event) => {
    const form = this.#form;
    const button = event.target instanceof Element ? event.target.closest('button[type="submit"], [name="add"]') : null;
    if (!form || !button || !form.contains(button)) return;

    const option = this.#selected;
    if (!option || !this.#isBigBundle(option)) return;

    event.preventDefault();
    event.stopPropagation();
    if (this.#adding) return;
    this.#adding = true;
    if (button instanceof HTMLButtonElement) button.disabled = true;

    const mainId = Number(new FormData(form).get('id'));
    const items = [{ id: mainId, quantity: Number(option.dataset.quantity) || 1 }];
    for (const id of (option.dataset.extras || '').split(',')) {
      if (id) items.push({ id: Number(id), quantity: 1 });
    }
    if (option.dataset.gift) items.push({ id: Number(option.dataset.gift), quantity: 1 });

    try {
      const code = option.dataset.discount;
      if (code) await fetch(`/discount/${encodeURIComponent(code)}?redirect=/cart.js`);

      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
        .filter(Boolean);
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message);

      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json());
      this.#showError('');
      this.dispatchEvent(
        new CartUpdateEvent({}, 'custom-bundle-picker', { source: 'custom-bundle-picker', itemCount: cart.item_count, sections: result.sections })
      );

      if (window.Zipify) {
        // Let the Zipify cart drawer show the full bundle.
        window.dispatchEvent(new CustomEvent('ocu:cart:open:updated'));
      } else {
        const drawer = document.querySelector('cart-drawer-component');
        if (drawer && 'open' in drawer && typeof drawer.open === 'function') drawer.open();
      }
    } catch (error) {
      console.error(error);
      this.#showError(error instanceof Error ? error.message : '');
    } finally {
      this.#adding = false;
      if (button instanceof HTMLButtonElement) button.disabled = false;
    }
  };

  /** @param {Event & { detail?: { data?: Record<string, any> } }} event */
  #onCartUpdate = async (event) => {
    const data = event.detail?.data;
    if (!data || data.didError || data.source !== 'product-form-component') return;
    if (String(data.productId) !== this.dataset.productId) return;

    const option = this.#selected;
    if (!option) return;

    const items = [];
    const wanted = Number(option.dataset.quantity) || 1;
    const added = Number(data.itemCount) || 0;
    const mainVariant = event.detail?.sourceId;
    if (added < wanted && mainVariant) items.push({ id: Number(mainVariant), quantity: wanted - added });

    for (const id of (option.dataset.extras || '').split(',')) {
      if (id) items.push({ id: Number(id), quantity: 1 });
    }
    if (option.dataset.gift) items.push({ id: Number(option.dataset.gift), quantity: 1 });

    const code = option.dataset.discount;
    if (items.length === 0 && !code) return;

    try {
      if (code) await fetch(`/discount/${encodeURIComponent(code)}?redirect=/cart.js`);

      let sections;
      if (items.length > 0) {
        const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
          .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
          .filter(Boolean);
        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: sectionIds.join(',') }),
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message);
        sections = result.sections;
      }

      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json());
      this.#showError('');
      this.dispatchEvent(
        new CartUpdateEvent({}, 'custom-bundle-picker', { source: 'custom-bundle-picker', itemCount: cart.item_count, sections })
      );
    } catch (error) {
      console.error(error);
      this.#showError(error instanceof Error ? error.message : '');
    }
  };

  /** @param {string} message */
  #showError(message) {
    const target = this.querySelector('.custom-bundle-picker__error');
    if (!(target instanceof HTMLElement)) return;
    target.textContent = message;
    target.hidden = !message;
  }
}

if (!customElements.get('custom-bundle-picker')) customElements.define('custom-bundle-picker', CustomBundlePicker);
